import React, { useState } from 'react'
import { Modal, Button, Typography, Space, Steps } from 'antd'
import {
  ShoppingCartOutlined,
  InboxOutlined,
  QuestionOutlined,
  PartitionOutlined,
  MergeCellsOutlined,
  SyncOutlined,
  LeftOutlined,
  RightOutlined,
} from '@ant-design/icons'
import { useTranslation } from 'react-i18next'

const { Title, Paragraph, Text } = Typography

/**
 * 訂單與出貨單概念說明 — 故事書風格 Wizard Modal
 */
export default function FulfillmentConceptModal({ visible, onClose }) {
  const { t } = useTranslation()
  const [currentStep, setCurrentStep] = useState(0)

  const prefix = 'concepts.fulfillments'

  const chapters = [
    {
      icon: <ShoppingCartOutlined style={{ fontSize: 28, color: '#1890ff' }} />,
      titleKey: `${prefix}.ch1_title`,
      paragraphs: [
        `${prefix}.ch1_p1`,
        `${prefix}.ch1_p2`,
      ],
    },
    {
      icon: <InboxOutlined style={{ fontSize: 28, color: '#52c41a' }} />,
      titleKey: `${prefix}.ch2_title`,
      paragraphs: [
        `${prefix}.ch2_p1`,
        `${prefix}.ch2_p2`,
        `${prefix}.ch2_p3`,
      ],
    },
    {
      icon: <QuestionOutlined style={{ fontSize: 28, color: '#faad14' }} />,
      titleKey: `${prefix}.ch3_title`,
      paragraphs: [
        `${prefix}.ch3_p1`,
        `${prefix}.ch3_p2`,
      ],
      bullets: [
        `${prefix}.ch3_b1`,
        `${prefix}.ch3_b2`,
        `${prefix}.ch3_b3`,
      ],
    },
    {
      icon: <PartitionOutlined style={{ fontSize: 28, color: '#eb2f96' }} />,
      titleKey: `${prefix}.ch4_title`,
      paragraphs: [
        `${prefix}.ch4_p1`,
      ],
      diagram: `${prefix}.ch4_diagram`,
      paragraphsAfter: [
        `${prefix}.ch4_p2`,
      ],
    },
    {
      icon: <MergeCellsOutlined style={{ fontSize: 28, color: '#722ed1' }} />,
      titleKey: `${prefix}.ch5_title`,
      paragraphs: [
        `${prefix}.ch5_p1`,
      ],
      diagram: `${prefix}.ch5_diagram`,
      paragraphsAfter: [
        `${prefix}.ch5_p2`,
      ],
    },
    {
      icon: <SyncOutlined style={{ fontSize: 28, color: '#13c2c2' }} />,
      titleKey: `${prefix}.ch6_title`,
      paragraphs: [
        `${prefix}.ch6_p1`,
        `${prefix}.ch6_p2`,
        `${prefix}.ch6_p3`,
      ],
    },
  ]

  const chapter = chapters[currentStep]
  const isFirst = currentStep === 0
  const isLast = currentStep === chapters.length - 1

  const handleClose = () => {
    setCurrentStep(0)
    onClose()
  }

  return (
    <Modal
      open={visible}
      onCancel={handleClose}
      closable={false}
      width={720}
      footer={
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Text type="secondary" style={{ fontSize: 13 }}>
            {currentStep + 1} / {chapters.length}
          </Text>
          <Space>
            {!isFirst && (
              <Button icon={<LeftOutlined />} onClick={() => setCurrentStep(s => s - 1)}>
                {t(`${prefix}.prev`)}
              </Button>
            )}
            {isLast ? (
              <Button type="primary" onClick={handleClose}>
                {t(`${prefix}.got_it`)}
              </Button>
            ) : (
              <Button type="primary" onClick={() => setCurrentStep(s => s + 1)}>
                {t(`${prefix}.next`)} <RightOutlined />
              </Button>
            )}
          </Space>
        </div>
      }
    >
      {/* 頂部 Steps 導航 */}
      <Steps
        current={currentStep}
        size="small"
        onChange={(step) => setCurrentStep(step)}
        style={{ marginBottom: 32 }}
        items={chapters.map((ch, i) => ({
          title: i === currentStep ? t(ch.titleKey) : '',
        }))}
      />

      {/* 章節標題 */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12, marginBottom: 24 }}>
        {chapter.icon}
        <Title level={3} style={{ margin: 0 }}>
          {t(chapter.titleKey)}
        </Title>
      </div>

      <div style={{ maxWidth: 560, margin: '0 auto' }}>
        {chapter.paragraphs?.map((key, i) => (
          <Paragraph key={i} style={{ fontSize: 15, lineHeight: 1.8, color: '#333' }}>
            {t(key)}
          </Paragraph>
        ))}

        {chapter.bullets && (
          <ul style={{ paddingLeft: 20, marginBottom: 16 }}>
            {chapter.bullets.map((key, i) => (
              <li key={i} style={{ fontSize: 15, lineHeight: 2, color: '#333' }}>
                {t(key)}
              </li>
            ))}
          </ul>
        )}

        {chapter.diagram && (
          <div style={{
            background: '#f6f8fa',
            border: '1px solid #e8e8e8',
            borderRadius: 8,
            padding: '20px 24px',
            margin: '16px 0',
            fontFamily: 'monospace',
            fontSize: 14,
            lineHeight: 1.8,
            whiteSpace: 'pre-line',
          }}>
            {t(chapter.diagram)}
          </div>
        )}

        {chapter.paragraphsAfter?.map((key, i) => (
          <Paragraph key={`after-${i}`} style={{ fontSize: 15, lineHeight: 1.8, color: '#333' }}>
            {t(key)}
          </Paragraph>
        ))}
      </div>
    </Modal>
  )
}
